import React from 'react';
import { Home, PlusCircle, BarChart3, PiggyBank, Wallet, Lock, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { usePreload } from '@/contexts/PreloadContext';

const navItems = [
  { id: 'dashboard', icon: Home, label: 'Home' },
  { id: 'split', icon: Users, label: 'Split' },
  { id: 'add', icon: PlusCircle, label: 'Add', isAction: true },
  { id: 'savings', icon: PiggyBank, label: 'Savings' },
  { id: 'projects', icon: Wallet, label: 'Budgets' },
];

export const MobileNav = ({ activeTab, onTabChange, onAddClick }) => {
  const { hasFeature } = useSubscription();
  const { preloadSplitGroups } = usePreload();
  
  const handleAddClick = () => {
    if (activeTab === 'projects') {
      // Dispatch event for budgets page to handle
      window.dispatchEvent(new CustomEvent('tripAddExpense'));
    } else if (activeTab === 'split') {
      // Dispatch event for split page to handle
      window.dispatchEvent(new CustomEvent('splitAddExpense'));
    } else {
      onAddClick();
    }
  };

  // Preload data on hover for faster navigation
  const handleMouseEnter = (itemId) => {
    if (itemId === 'split') {
      preloadSplitGroups();
    }
  };

  const navElements = [];
  for (let i = 0; i < navItems.length; i++) {
    const item = navItems[i];
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    const isLocked = item.requiresFeature && !hasFeature(item.requiresFeature);
    
    if (item.isAction) {
      navElements.push(
        <button
          key={item.id}
          onClick={handleAddClick}
          className="flex flex-col items-center justify-center -mt-6"
          data-testid="add-transaction-btn"
        >
          <div className="w-14 h-14 rounded-full bg-gradient-primary flex items-center justify-center shadow-elevated hover:scale-105 active:scale-95 transition-transform">
            <Icon className="w-7 h-7 text-primary-foreground" />
          </div>
        </button>
      );
    } else {
      navElements.push(
        <button
          key={item.id}
          onClick={() => onTabChange(item.id)}
          onMouseEnter={() => handleMouseEnter(item.id)}
          onTouchStart={() => handleMouseEnter(item.id)}
          data-testid={`nav-${item.id}`}
          className={cn(
            'flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-colors min-w-[60px] relative',
            isActive
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <div className="relative">
            <Icon
              className={cn(
                'w-5 h-5 mb-1 transition-transform',
                isActive && 'scale-110'
              )}
            />
            {isLocked && (
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-500 flex items-center justify-center">
                <Lock className="w-2 h-2 text-white" />
              </div>
            )}
          </div>
          <span className="text-[10px] font-medium">{item.label}</span>
          {isActive && (
            <div className="absolute bottom-1 w-1 h-1 rounded-full bg-primary" />
          )}
        </button>
      );
    }
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[100] bg-card/95 backdrop-blur-lg border-t border-border shadow-elevated safe-area-bottom">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-2">
        {navElements}
      </div>
    </nav>
  );
};
